import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  CHIP_GAP_PX,
  getLabelLayout,
  MAX_LABEL_LENGTH,
  measureChipWidth,
} from './drive-label-chips-cell.utils';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Popover from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

const EMPTY_HIGHLIGHT_LABELS: string[] = [];

interface FilesPreviewSectionProps {
  affectedDocs: Array<{
    id: string;
    title: string;
    labelTitles?: string[] | null;
  }>;
  highlightLabels?: string[];
}

export default function FilesPreviewSection({
  affectedDocs,
  highlightLabels = EMPTY_HIGHLIGHT_LABELS,
}: FilesPreviewSectionProps) {
  const highlightLabelSet = useMemo(
    () => new Set(highlightLabels),
    [highlightLabels]
  );

  const docsWithSortedLabels = useMemo(
    () =>
      affectedDocs.map((doc) => {
        const sortedLabelTitles = [...(doc.labelTitles ?? [])].sort((a, b) => {
          const aStr = a || '';
          const bStr = b || '';
          const aHighlighted =
            highlightLabelSet.size > 0 && highlightLabelSet.has(aStr);
          const bHighlighted =
            highlightLabelSet.size > 0 && highlightLabelSet.has(bStr);

          if (aHighlighted !== bHighlighted) {
            return aHighlighted ? 1 : -1;
          }

          return aStr.localeCompare(bStr);
        });

        return {
          ...doc,
          sortedLabelTitles,
        };
      }),
    [affectedDocs, highlightLabelSet]
  );

  return (
    <Box
      sx={{
        width: '100%',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        overflow: 'hidden',
        boxSizing: 'border-box',
        minHeight: 290,
        maxHeight: 290,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Stack direction="row" alignItems="center" sx={{ px: 1.5, py: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          Files Preview
        </Typography>
      </Stack>
      <Divider />

      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {docsWithSortedLabels.map((doc, index) => (
          <Box
            key={doc.id}
            sx={{
              px: 1.5,
              py: 0.75,
              borderBottom:
                index < affectedDocs.length - 1 ? '1px solid' : 'none',
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              minWidth: 0,
            }}
          >
            <Tooltip title={doc.title}>
              <Typography
                variant="body2"
                sx={{
                  maxWidth: '100px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                }}
              >
                {doc.title}
              </Typography>
            </Tooltip>

            <FilesPreviewLabels
              labels={doc.sortedLabelTitles}
              highlightLabelSet={highlightLabelSet}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
}

interface FilesPreviewLabelsProps {
  labels: string[];
  highlightLabelSet: ReadonlySet<string>;
}

function FilesPreviewLabels({
  labels,
  highlightLabelSet,
}: FilesPreviewLabelsProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [layout, setLayout] = useState<{
    visibleCount: number;
    truncatedLabelMaxWidth?: number;
  }>({
    visibleCount: labels.length,
  });
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const leaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const updateLayout = useCallback(() => {
    const container = containerRef.current;
    if (!container || !labels.length) {
      return;
    }

    const containerWidth = container.clientWidth;
    if (containerWidth <= 0) {
      return;
    }

    // Estimate widths via Canvas 2D instead of mounting hidden DOM chips —
    // avoids O(docs x labels) hidden nodes/layout reads for large preview lists.
    // Measure in original order; reverse to give RTL priority to the last (rightmost) labels.
    const chipWidths = labels.map((label) => {
      const isHighlighted =
        highlightLabelSet.size > 0 && highlightLabelSet.has(label);
      const displayLabel = isHighlighted ? `- ${label}` : label;
      return measureChipWidth(displayLabel, MAX_LABEL_LENGTH);
    });

    const reversedChipWidths = [...chipWidths].reverse();

    const countChipWidths = new Map<number, number>();
    for (let count = 1; count < labels.length; count++) {
      countChipWidths.set(count, measureChipWidth(`+${count}`));
    }

    setLayout((prev) => {
      const next = getLabelLayout(
        reversedChipWidths,
        containerWidth,
        countChipWidths,
        CHIP_GAP_PX
      );

      if (
        prev.visibleCount === next.visibleCount &&
        prev.truncatedLabelMaxWidth === next.truncatedLabelMaxWidth
      ) {
        return prev;
      }

      return next;
    });
  }, [labels, highlightLabelSet]);

  useLayoutEffect(() => {
    updateLayout();

    const container = containerRef.current;
    if (!container) {
      return;
    }

    const resizeObserver = new ResizeObserver(updateLayout);
    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, [updateLayout]);

  // Clean up the leave-delay timer on unmount.
  useEffect(() => {
    return () => {
      if (leaveTimerRef.current) {
        clearTimeout(leaveTimerRef.current);
      }
    };
  }, []);

  if (!labels.length) {
    return null;
  }

  const { visibleCount, truncatedLabelMaxWidth } = layout;
  const hiddenCount = labels.length - visibleCount;
  const hasOverflow = hiddenCount > 0;
  // RTL: show the last visibleCount labels so rightmost chips are prioritised
  const visibleLabels = labels.slice(-visibleCount);
  const useTruncatable = visibleCount === 1;
  const handleBadgeMouseEnter = (e: React.MouseEvent<HTMLDivElement>) => {
    if (leaveTimerRef.current) {
      clearTimeout(leaveTimerRef.current);
      leaveTimerRef.current = null;
    }
    setAnchorEl(e.currentTarget);
  };

  const handleBadgeMouseLeave = () => {
    leaveTimerRef.current = setTimeout(() => {
      setAnchorEl(null);
      leaveTimerRef.current = null;
    }, 200);
  };

  return (
    <>
      <Box sx={{ position: 'relative', flex: 1, minWidth: 0 }}>
        {/* Visible chips — RTL: flex-end pushes the group to the right; +N badge is on the left */}
        <Stack
          ref={containerRef}
          direction="row"
          alignItems="center"
          gap={1}
          sx={{
            width: '100%',
            overflow: 'hidden',
            justifyContent: 'flex-end',
          }}
        >
          {hasOverflow && (
            <Chip
              label={`+${hiddenCount}`}
              size="small"
              sx={{ flexShrink: 0, cursor: 'default' }}
              onMouseEnter={handleBadgeMouseEnter}
              onMouseLeave={handleBadgeMouseLeave}
            />
          )}
          {visibleLabels.map((label, index) => {
            const isHighlighted =
              highlightLabelSet.size > 0 && highlightLabelSet.has(label);
            const displayLabel = isHighlighted ? `- ${label}` : label;

            return (
              <Chip
                // eslint-disable-next-line react-x/no-array-index-key
                key={`${index}-${label}`}
                label={displayLabel}
                size="small"
                variant={isHighlighted ? 'outlined' : 'filled'}
                color={isHighlighted ? 'error' : 'default'}
                sx={{
                  flexShrink: 0,
                  ...(useTruncatable
                    ? {
                        minWidth: 0,
                        maxWidth:
                          hasOverflow && truncatedLabelMaxWidth !== undefined
                            ? truncatedLabelMaxWidth
                            : `min(100%, ${MAX_LABEL_LENGTH}ch)`,
                        '& .MuiChip-label': {
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          ...(!isHighlighted && { color: 'text.disabled' }),
                        },
                      }
                    : {
                        maxWidth: `${MAX_LABEL_LENGTH}ch`,
                        ...(!isHighlighted && {
                          '& .MuiChip-label': { color: 'text.disabled' },
                        }),
                      }),
                  ...(!isHighlighted && {
                    bgcolor: 'action.disabledBackground',
                  }),
                }}
              />
            );
          })}
        </Stack>
      </Box>

      {/* Popover (not Tooltip) so it stays visible when the file list is scrolled.
          Tooltip closes on ancestor scroll via popper's hide middleware; Popover is a
          Modal rendered in a body portal and only closes on explicit onClose. */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
        disableScrollLock
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        sx={{ pointerEvents: 'none' }}
        slotProps={{
          paper: {
            sx: {
              maxWidth: 550,
              bgcolor: 'primary.light',
              color: 'text.primary',
              boxShadow: 1,
              p: 1,
              borderRadius: 1,
            },
          },
        }}
      >
        <Stack gap={0.5} flexDirection="row" flexWrap="wrap">
          {labels.map((label, index) => {
            const isHighlighted =
              highlightLabelSet.size > 0 && highlightLabelSet.has(label);
            return (
              <Chip
                // eslint-disable-next-line react-x/no-array-index-key
                key={`popover-${index}-${label}`}
                label={isHighlighted ? `- ${label}` : label}
                size="small"
                variant={isHighlighted ? 'outlined' : 'filled'}
                sx={{
                  ...(!isHighlighted && {
                    bgcolor: 'action.disabledBackground',
                    '& .MuiChip-label': { color: 'text.disabled' },
                  }),
                }}
              />
            );
          })}
        </Stack>
      </Popover>
    </>
  );
}
